import classNames from 'classnames';
import debounce from 'lodash/debounce';
import { arrayOf, bool, func, string } from 'prop-types';
import React from 'react';

import { reactTableColumnShape } from '../../prop-types';
import withApiRequest from '../../REST/with-api-request';
import { flash } from '../../utils/flash';
import { setItem } from '../../utils/local-storage';
import storedColumns from '../../utils/stored-columns';
import SelectTable from './SelectTable';
import Table from './Table';
import TableFiltersModal from './TableFiltersModal';
import { transformTableStateToSearchParams } from './utils';

class AsyncTable extends React.Component {
  constructor(props) {
    super(props);
    const { allColumns, columns, storedName } = this.props;
    this.state = {
      data: [],
      pages: null,
      currentColumns: storedColumns(allColumns, columns, storedName),
      savedColumns: storedColumns(allColumns, columns, storedName),
      count: 0,
      tableState: {},
    };
  }

  handleDataFetch = async tableState => {
    const { allColumns, queryFn, setData, setTableFilters } = this.props;
    this.setState({
      tableState,
    });
    const tableParams = transformTableStateToSearchParams(
      tableState,
      allColumns,
    );
    const { filters, ...rest } = tableParams;
    const { results, count } = await this.props.onRequest(queryFn, {
      search: {
        _paginate: 'number',
        ...filters,
        ...rest,
      },
    });

    setTableFilters(filters);
    setData(results);

    this.setState({
      data: results,
      pages: Math.ceil(count / tableState.pageSize),
      count,
    });
  };

  handleCheckboxChange = e => {
    const { allColumns } = this.props;
    const { currentColumns } = this.state;
    const chosenColumn = allColumns.find(column =>
      column.Label
        ? column.Label === e.target.id
        : column.Header === e.target.id,
    );
    let newColumns = [...currentColumns];
    if (e.target.checked === true) {
      newColumns = [...currentColumns, chosenColumn];
    } else {
      newColumns = newColumns.filter(column =>
        column.Label
          ? column.Label !== chosenColumn.Label
          : column.Header !== chosenColumn.Header,
      );
    }
    this.setState({ currentColumns: newColumns });
  };

  handleSave = () => {
    const { currentColumns } = this.state;
    const { storedName } = this.props;
    this.setState({ savedColumns: currentColumns });
    setItem(storedName, JSON.stringify(currentColumns));
    flash('Table columns saved successfully', 'success');
  };

  handleReset = () => {
    this.setState({
      currentColumns: [...this.props.columns],
    });
  };

  renderSelectTable = () => {
    const { data, pages, count, tableState } = this.state;
    const { columns, loading, allowSelection, allowFilterView, ...rest } =
      this.props;
    return (
      <SelectTable
        {...rest}
        columns={allowFilterView ? this.state.savedColumns : columns}
        count={count}
        manual
        filterable
        data={data}
        pages={pages}
        loading={loading}
        tableState={tableState}
        // Added to improve performance as this would call the API
        // every 500ms only when user tries searching/filtering
        onFetchData={debounce(this.handleDataFetch, 500)}
      />
    );
  };

  render() {
    const { data, pages } = this.state;
    const {
      columns,
      loading,
      allowSelection,
      allowFilterView,
      allColumns,
      ...rest
    } = this.props;

    return (
      <>
        <div
          className={
            allowSelection && allowFilterView
              ? classNames('filters-modal-container', 'mb-35')
              : 'filters-modal-container'
          }
        >
          {allowFilterView && (
            <TableFiltersModal
              currentColumns={this.state.currentColumns}
              allColumns={allColumns}
              onHandleChange={this.handleCheckboxChange}
              onHandleReset={this.handleReset}
              onHandleSave={this.handleSave}
            />
          )}
        </div>
        {allowSelection && this.renderSelectTable()}
        {!allowSelection && (
          <Table
            {...rest}
            columns={allowFilterView ? this.state.savedColumns : columns}
            manual
            filterable
            data={data}
            pages={pages}
            loading={loading}
            // Added to improve performance as this would call the API
            // every 500ms only when user tries searching/filtering
            onFetchData={debounce(this.handleDataFetch, 500)}
          />
        )}
      </>
    );
  }
}

AsyncTable.propTypes = {
  columns: arrayOf(reactTableColumnShape.isRequired).isRequired,
  allColumns: arrayOf(reactTableColumnShape.isRequired).isRequired,
  storedName: string,
  queryFn: func.isRequired,
  allowSelection: bool,
  allowFilterView: bool,

  // HOC props
  loading: bool.isRequired,
  onRequest: func.isRequired,
  setData: func,
  setTableFilters: func,
};

AsyncTable.defaultProps = {
  allowSelection: false,
  setData: () => {},
  setTableFilters: () => {},
  allowFilterView: false,
  storedName: '',
};

export default withApiRequest({
  query: (fn, ...args) => fn(...args),
  error: 'flash',
})(AsyncTable);
